import React from 'react';
import { RotateCcw } from 'lucide-react';
import { Checkbox, SegmentedControl, Switch } from '../../../components/ui';
import type { LinkKind, NetworkLink, RelationKind } from '../types';
import { DEFAULT_FILTERS, KIND_LABEL, NetworkFilters, TYPE_LABEL, activeFilterCount } from './networkModel';
import { TieSwatch } from './ui';

const KINDS: LinkKind[] = ['relationship', 'employment', 'hierarchy', 'co_affiliation'];
const TYPES: RelationKind[] = ['os', 'oo', 'ss'];

const KIND_HINT: Record<LinkKind, string> = {
  relationship: 'Recorded by analysts or accepted AI leads',
  employment: 'People in organizations',
  hierarchy: 'Parent and child organizations',
  co_affiliation: 'People who share an organization',
};

export const FiltersPanel: React.FC<{
  filters: NetworkFilters;
  onChange: (next: NetworkFilters) => void;
  /** all links in the data, for counts */
  links: NetworkLink[];
  timeOn: boolean;
}> = ({ filters, onChange, links, timeOn }) => {
  const set = (patch: Partial<NetworkFilters>) => onChange({ ...filters, ...patch });
  const kindCount = (k: LinkKind) => links.filter((l) => l.kind === k && (filters.includePast || timeOn || l.isCurrent !== false)).length;
  const typeCount = (t: RelationKind) => links.filter((l) => l.type === t && (filters.includePast || timeOn || l.isCurrent !== false)).length;
  const count = activeFilterCount(filters);
  const row = 'flex h-9 w-full items-center gap-2.5 rounded-xl px-2 text-left text-[13px] transition-colors hover:bg-neutral-50';

  return (
    <div className="space-y-4">
      <section>
        <p className="mb-1.5 px-2 text-[12px] text-neutral-500">Entities</p>
        <div className="px-2">
          <SegmentedControl
            label="Entity type"
            value={filters.entities}
            onChange={(entities) => set({ entities })}
            className="w-full [&>button]:flex-1 [&>button]:justify-center"
            items={[
              { value: 'all', label: 'All' },
              { value: 'stakeholder', label: 'People' },
              { value: 'organization', label: 'Organizations' },
            ]}
          />
        </div>
      </section>

      <section>
        <p className="mb-1 px-2 text-[12px] text-neutral-500">Tie types</p>
        {TYPES.map((t) => (
          <Checkbox
            key={t}
            className={row}
            checked={filters.types[t]}
            onChange={(e) => set({ types: { ...filters.types, [t]: e.target.checked } })}
            label={
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <TieSwatch type={t} className={filters.types[t] ? '' : 'opacity-30'} />
                <span className={`flex-1 truncate ${filters.types[t] ? 'text-ink' : 'text-neutral-400'}`}>{TYPE_LABEL[t]}</span>
                <span className="text-[12px] tabular-nums text-neutral-400">{typeCount(t)}</span>
              </span>
            }
          />
        ))}
      </section>

      <section>
        <p className="mb-1.5 px-2 text-[12px] text-neutral-500">Sources</p>
        <div className="flex flex-wrap gap-1.5 px-2">
          {KINDS.map((k) => {
            const on = filters.kinds[k];
            return (
              <button
                key={k}
                type="button"
                aria-pressed={on}
                title={KIND_HINT[k]}
                onClick={() => set({ kinds: { ...filters.kinds, [k]: !on } })}
                className={`inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[12px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
                  on ? 'bg-neutral-100 text-ink ring-1 ring-inset ring-neutral-200' : 'bg-white text-neutral-400 ring-1 ring-inset ring-neutral-200 hover:text-ink'
                }`}
              >
                {KIND_LABEL[k]}
                <span className="tabular-nums text-neutral-400">{kindCount(k)}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="space-y-0.5">
        <div className={row}>
          <span className="flex-1" title="Ties that have ended">
            Past ties
          </span>
          <Switch label="Include past ties" checked={filters.includePast || timeOn} disabled={timeOn} onChange={(includePast) => set({ includePast })} />
        </div>
        <div className={row}>
          <span className="flex-1" title="Entities with no ties in view">
            Hide isolated
          </span>
          <Switch label="Hide isolated entities" checked={filters.hideIsolated} onChange={(hideIsolated) => set({ hideIsolated })} />
        </div>
      </section>

      {count > 0 && (
        <button
          type="button"
          onClick={() => onChange(DEFAULT_FILTERS)}
          className="mx-2 inline-flex h-7 items-center gap-1.5 rounded-full px-2 text-[12px] text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-ink"
        >
          <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} />
          Reset filters
        </button>
      )}
    </div>
  );
};
